import { Dayjs } from 'dayjs';

// A page as returned by `GET /api/archives/:id/pages`
// (services/documentPages.js#serializePage, verified with curl).
export type DocumentPage = {
  id: string;
  document_id: string;
  position: number;
  mime_type: string;
  byte_size: number;
  verification_url: string | null;
  /** Path relative to the API origin (`/api/image_documents/…`). */
  image_url: string;
};

// What the wizard knows about each row of the page list: either a
// page already on the server, or an in-progress (or failed) upload that is not
// one yet. The original name and size only exist for what was uploaded in
// this session: the backend does not store them (it re-encodes the image and
// gives it its own name), so a resumed draft shows a plain "Page N".
export type PageRow =
  | {
      kind: 'page';
      key: string;
      page: DocumentPage;
      fileName?: string;
    }
  | {
      kind: 'upload';
      key: string;
      fileName: string;
      byteSize: number;
      /** 0–1 while uploading; `null` before starting. */
      progress: number | null;
      error?: string;
    };

// What `qrCodes.summarise` says about a set of pages. The listing endpoint
// does not return it (a difference from what was expected), so it is computed
// on the client with the same rule: no URL → `none`; a single distinct URL
// → `consistent`; several → `mismatch`.
export type QrSummary =
  | { status: 'none'; url: null }
  | { status: 'consistent'; url: string }
  | { status: 'mismatch'; url: null; urls: string[] };

// A document as returned by `GET /api/archives/document?id=`
// (verified with curl on 23/08): draft or archived. `archiver`,
// `archiving_date` and `file_path` always arrive; creation (`POST /`) omits
// them, hence they are optional.
export type DraftDocument = {
  id: string;
  title: string;
  issuing_date: string | null;
  service: string;
  issuer: string;
  archiver?: string;
  confidential: boolean;
  verification_url: string;
  status: 'draft' | 'archived';
  archiving_date?: string | null;
  file_path?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

// `GET /archives/document?id=` returns the document and, separately, the path
// of the assembled file (or of the single file, for those archived through the
// previous path), relative to the API origin. A draft has no file.
export type DocumentView = {
  document: DraftDocument;
  image: string | null;
};

export type MetadataValues = {
  title: string;
  issuingDate: Dayjs | null;
  service: string;
  issuer: string;
  confidential: boolean;
};

export type MetadataErrors = Partial<Record<keyof MetadataValues, string>>;

export const EMPTY_METADATA: MetadataValues = {
  title: '',
  issuingDate: null,
  service: '',
  issuer: '',
  confidential: false,
};

export type WizardStep = 1 | 2 | 3;
